/**
 * DocHealthPage — admin view of Help article feedback. Ranks Help sections by
 * the number of "No" (not-helpful) votes so admins know what to rewrite.
 * Data comes from GET /api/help/feedback/summary (tenant-scoped, admin-only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsDown, ThumbsUp, Loader2, BookOpen, ArrowUpRight, HeartPulse, Wand2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { HELP_SECTIONS } from '@/pages/HelpPage';

export default function DocHealthPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const titleFor = useMemo(() => {
    const map = {};
    (HELP_SECTIONS || []).forEach((s) => { map[s.id] = s.title; });
    return map;
  }, []);

  // Doc Rewrite Assist
  const [rewrite, setRewrite] = useState(null); // { article_id, title, draft }
  const [rewriteLoading, setRewriteLoading] = useState('');
  const [rewriteSaving, setRewriteSaving] = useState(false);

  const draftRewrite = async (sectionId, title) => {
    setRewriteLoading(sectionId);
    try {
      const r = await api.rewriteHelpArticle(sectionId);
      setRewrite({ article_id: sectionId, title, draft: r.draft });
    } catch (err) { toast.error(err.message || 'Rewrite failed'); }
    finally { setRewriteLoading(''); }
  };
  const saveRewrite = async () => {
    if (!rewrite) return;
    setRewriteSaving(true);
    try {
      await api.updateHelpArticle(rewrite.article_id, { content: rewrite.draft });
      toast.success('Article updated with the rewrite');
      setRewrite(null);
    } catch (err) { toast.error(err.message); }
    finally { setRewriteSaving(false); }
  };
  const copyRewrite = async () => {
    try { await navigator.clipboard.writeText(rewrite.draft); toast.success('Copied'); } catch { /* clipboard blocked */ }
  };

  useEffect(() => {
    api.getHelpFeedbackSummary()
      .then((r) => setItems(r?.items || []))
      .catch((e) => toast.error(e.message || 'Failed to load doc health'))
      .finally(() => setLoading(false));
  }, []);

  // Rank: most "No" votes first, then lowest helpful ratio.
  const ranked = useMemo(() => {
    return [...items]
      .map((it) => {
        const total = (it.helpful || 0) + (it.not_helpful || 0);
        const ratio = total > 0 ? it.helpful / total : 1;
        return { ...it, total, ratio, title: titleFor[it.section_id] || it.section_id };
      })
      .sort((a, b) => (b.not_helpful - a.not_helpful) || (a.ratio - b.ratio));
  }, [items, titleFor]);

  const totals = useMemo(() => ranked.reduce(
    (acc, it) => ({ helpful: acc.helpful + it.helpful, not_helpful: acc.not_helpful + it.not_helpful }),
    { helpful: 0, not_helpful: 0 }
  ), [ranked]);

  if (loading) {
    return (
      <div className="p-6" data-testid="doc-health-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="doc-health-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
          <HeartPulse className="w-6 h-6 text-rose-400" /> Doc Health
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Help articles ranked by "No" (not helpful) votes — the ones at the top are the best candidates to rewrite.
        </p>
      </div>

      <div className="flex gap-3">
        <div className="glass-card rounded-2xl px-4 py-3 flex items-center gap-2" data-testid="doc-health-total-helpful">
          <ThumbsUp className="w-4 h-4 text-emerald-400" />
          <span className="text-lg font-heading font-bold text-foreground">{totals.helpful}</span>
          <span className="text-xs text-muted-foreground">helpful</span>
        </div>
        <div className="glass-card rounded-2xl px-4 py-3 flex items-center gap-2" data-testid="doc-health-total-not-helpful">
          <ThumbsDown className="w-4 h-4 text-red-400" />
          <span className="text-lg font-heading font-bold text-foreground">{totals.not_helpful}</span>
          <span className="text-xs text-muted-foreground">not helpful</span>
        </div>
      </div>

      {ranked.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="doc-health-empty">
          <BookOpen className="w-6 h-6 mx-auto mb-2 text-muted-foreground/70" />
          No Help feedback yet. Once users rate articles with "Was this helpful?", the results appear here.
        </div>
      ) : (
        <div className="space-y-2" data-testid="doc-health-list">
          {ranked.map((it) => {
            const pct = Math.round(it.ratio * 100);
            const needsWork = it.not_helpful > 0 && it.ratio < 0.6;
            return (
              <div
                key={it.section_id}
                className={`glass-card rounded-2xl p-4 flex items-center gap-4 ${needsWork ? 'border-red-500/30' : ''}`}
                data-testid={`doc-health-row-${it.section_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground truncate">{it.title}</span>
                    {needsWork && <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-300">Needs work</Badge>}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-muted/40 overflow-hidden max-w-xs">
                    <div className={`h-full ${pct >= 60 ? 'bg-emerald-400' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{pct}% found it helpful · {it.total} vote{it.total === 1 ? '' : 's'}</p>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <span className="inline-flex items-center gap-1 text-emerald-400"><ThumbsUp className="w-3.5 h-3.5" /> {it.helpful}</span>
                  <span className="inline-flex items-center gap-1 text-red-400"><ThumbsDown className="w-3.5 h-3.5" /> {it.not_helpful}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 rounded-lg text-[10px]"
                  disabled={rewriteLoading === it.section_id}
                  onClick={() => draftRewrite(it.section_id, it.title)}
                  data-testid={`doc-health-rewrite-${it.section_id}`}
                >
                  {rewriteLoading === it.section_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Draft rewrite
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 rounded-lg text-[10px]"
                  onClick={() => navigate(`/help?s=${it.section_id}`)}
                  data-testid={`doc-health-open-${it.section_id}`}
                >
                  Open <ArrowUpRight className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Doc Rewrite Assist */}
      <Dialog open={!!rewrite} onOpenChange={(v) => { if (!v) setRewrite(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl" data-testid="doc-rewrite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-primary" /> AI rewrite — {rewrite?.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Review the AI-drafted rewrite. Save to publish it to the live article, or copy it to edit manually.</p>
          <Textarea
            value={rewrite?.draft || ''}
            onChange={(e) => setRewrite((r) => ({ ...r, draft: e.target.value }))}
            rows={16}
            className="rounded-lg bg-muted/50 border-border/50 font-mono text-xs mt-2"
            data-testid="doc-rewrite-content"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button onClick={saveRewrite} disabled={rewriteSaving} className="flex-1 rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="doc-rewrite-save">
              <Save className="w-4 h-4 mr-2" />{rewriteSaving ? 'Saving…' : 'Save & Publish'}
            </Button>
            <Button variant="outline" onClick={copyRewrite} className="rounded-lg" data-testid="doc-rewrite-copy">Copy</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
